import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import UnifiedContactModal from './UnifiedContactModal';
import SeasonalSupportBanner from './SeasonalSupportBanner';

const Hero = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section className={`pt-20 ${isMobile ? 'pb-8' : 'pb-16'} bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className={`text-4xl lg:text-6xl font-bold text-gray-900 ${isMobile ? 'mb-3' : 'mb-6'} leading-tight`}>
              Industry Proven
              <span className="text-ctc-blue block">Water Solutions</span>
            </h1>
            <p className={`${isMobile ? 'text-lg mb-4' : 'text-xl mb-8'} text-gray-600 leading-relaxed`}>
              Trusted by Farmers, Engineers, & Operators globally for superior sulfur burners, hydrocoolers,
              and filtration systems that maximize water quality.
            </p>

            <div className={`flex flex-col sm:flex-row gap-4 ${isMobile ? 'mb-4' : 'mb-8'}`}>
              <motion.a
                href="#sulfur-burners"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-8 py-4 rounded-lg font-semibold hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-2"
              >
                <span>Explore Our Products</span>
                <ArrowRight size={20} />
              </motion.a>
              <motion.button
                onClick={() => setIsContactModalOpen(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-ctc-blue text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-ctc-blue hover:text-white transition-all duration-300 text-center"
              >
                Get Quote
              </motion.button>
            </div>

            <div className={`${isMobile ? 'space-y-1.5' : 'space-y-3'}`}>
              {[
                'Industry-leading sulfur burner technology',
                'Proven to increase crop Quality & Yield',
                'Trusted by over 1,000 customers to Protect & Enhance water'
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 + 0.5 }}
                  className="flex items-center space-x-3"
                >
                  <CheckCircle className="text-green-500 flex-shrink-0" size={isMobile ? 16 : 20} />
                  <span className={`text-gray-700 ${isMobile ? 'text-sm' : ''}`}>{benefit}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Hero Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className={`bg-gradient-to-br from-ctc-silver-light to-ctc-silver rounded-2xl p-4 ${isMobile ? 'h-[300px]' : 'h-[500px]'} flex items-center justify-center shadow-xl`}>
              <img
                src="/Sulfur Burner Diagram Picture - View 2.png"
                alt="CTC Waterworks sulfur burner technical diagram showing advanced engineering design"
                className="w-full h-full object-contain scale-110"
              />
            </div>
            
            {/* Floating Stats - Fixed mobile positioning */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className={`absolute -bottom-6 -left-6 lg:-left-12 bg-white rounded-xl shadow-xl ${isMobile ? 'p-3' : 'p-6'} border border-ctc-silver`}
            >
              <div className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-ctc-blue`}>40+ Years</div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>Of Trusted Results</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className={`absolute -top-2 lg:-top-6 -right-6 bg-white rounded-xl shadow-xl ${isMobile ? 'p-3' : 'p-6'} border border-ctc-silver`}
            >
              <div className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-green-600`}>1000+</div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>Happy Customers</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Seasonal Support Banner */}
        <SeasonalSupportBanner />
      </div>

      {/* Unified Contact Modal */}
      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="general"
        formSource="hero_cta"
      />
    </section>
  );
};

export default Hero;